import { useQuery } from '@apollo/client';
import { graphql } from '@client/graphql/types';
import { useMemo } from 'react';
import { DateRange, TrendsAnalyticsService } from '../../common/reservation-analytic-service';

const QUERY = graphql(`
  query analytic_nailAppointment_getOverviewMetrics($branchId: ID!, $startTime: DateTime!, $endTime: DateTime!) {
    analytic_nailAppointment_getOverviewMetrics(branchId: $branchId, startTime: $startTime, endTime: $endTime) {
      totalRevenue
      averageRevenuePerAppointment
      totalConfirmedAppointments
      bookingPerformanceRate
    }
  }
`);

export default function useGetAppointmentOverviewMetric(branchId: string, dateRange: DateRange) {
  const previousDateRange = useMemo(() => TrendsAnalyticsService.getPreviousDateRange(dateRange), [dateRange]);

  // Current period query
  const {
    data: currentData,
    refetch: currentRefetch,
    loading: currentLoading,
    error: currentError,
  } = useQuery(QUERY, {
    variables: { branchId, startTime: dateRange.startDate, endTime: dateRange.endDate },
  });

  // Previous period query
  const {
    data: previousData,
    refetch: previousRefetch,
    loading: previousLoading,
    error: previousError,
  } = useQuery(QUERY, {
    variables: {
      branchId,
      startTime: previousDateRange.startDate,
      endTime: previousDateRange.endDate,
    },
  });

  // Computed values
  const analytics = useMemo(() => {
    if (
      !currentData?.analytic_nailAppointment_getOverviewMetrics ||
      !previousData?.analytic_nailAppointment_getOverviewMetrics
    ) {
      return null;
    }

    const current = currentData.analytic_nailAppointment_getOverviewMetrics;
    const previous = previousData.analytic_nailAppointment_getOverviewMetrics;
    const trends = {
      totalRevenue: TrendsAnalyticsService.calculateTrend(current.totalRevenue, previous.totalRevenue),
      averageRevenuePerAppointment: TrendsAnalyticsService.calculateTrend(
        current.averageRevenuePerAppointment,
        previous.averageRevenuePerAppointment,
      ),
      totalConfirmedAppointments: TrendsAnalyticsService.calculateTrend(
        current.totalConfirmedAppointments,
        previous.totalConfirmedAppointments,
      ),
      bookingPerformanceRate: TrendsAnalyticsService.calculateTrend(
        current.bookingPerformanceRate,
        previous.bookingPerformanceRate,
      ),
    };

    return {
      current,
      previous,
      trends,
    };
  }, [currentData, previousData]);

  const isLoading = currentLoading || previousLoading;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const error = currentError || previousError;

  return {
    analytics,
    isLoading,
    error,
    refetch: () => {
      // Refetch both queries
      return Promise.all([currentRefetch(), previousRefetch()]);
    },
  };
}
